import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ApiError, errorMessage } from '../../api.js';
import type { LinkItem } from '../../components/LinkPicker.js';
import { RecordSearch } from '../../components/RecordSearch.js';
import { useToast } from '../../components/Toast.js';
import { Dialog, Notice } from '../../components/ui.js';
import { NAV } from '../../nav.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import { useSignedIn } from '../../session.js';
import { loadContact, type ContactSheetData } from '../people/data.js';
import { importHubSpotContact } from './data.js';
import { useLead, usePeopleBack } from './LeadSheet.js';
import { StartLead } from './StartLead.js';

/** Where a new lead opens: its sheet, over the leads list (no second form). */
export const leadSheetPath = (id: string) => `/people/leads/${id}`;

/** Loads a contact for Start the lead. */
function useContact(id: string | null) {
  const { user, refresh } = useSignedIn();
  const { store } = useOffline();
  const [data, setData] = useState<ContactSheetData | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!id) return;
    let current = true;
    loadContact(id, store, { id: user.id, name: `${user.firstName} ${user.lastName}` })
      .then((loaded) => {
        if (!current) return;
        if (loaded) setData(loaded);
        else setError('This contact does not exist, or it was deleted.');
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [id, store, user, refresh]);
  return { data, error };
}

/**
 * Add lead (brief section 6.7, decision of Nov 2, 2026): find the person
 * among the portal's contacts and HubSpot's (a HubSpot contact is imported
 * as already synced, never a duplicate), then block 2 directly. Not found:
 * New person, block 1 and block 2 in the same window (Add contact on Yes).
 */
export function AddLead() {
  const navigate = useNavigate();
  const location = useLocation();
  const [picked, setPicked] = useState<string | null>(null);
  const { data, error } = useContact(picked);
  const [failed, setFailed] = useState<string | null>(null);
  const back = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/', { replace: true });
  };

  /** A portal contact, or a HubSpot one imported first (never a duplicate). */
  async function choose(item: LinkItem) {
    setFailed(null);
    try {
      if (item.id) setPicked(item.id);
      else if (item.hubspotId) setPicked(await importHubSpotContact(item.hubspotId));
    } catch (err) {
      setFailed(errorMessage(err));
    }
  }

  if (picked && data) {
    return (
      <StartLead
        contact={data.contact}
        leads={data.leads}
        eventCode={data.eventCode}
        onCancel={back}
        onCreated={(id) => navigate(leadSheetPath(id), { replace: true })}
      />
    );
  }
  return (
    <Dialog
      title="New lead"
      onClose={back}
      footer={
        <button type="button" className="btn" onClick={back}>
          Cancel
        </button>
      }
    >
      <p style={{ margin: 0 }}>
        Find the person: a contact of the portal, or a contact already in HubSpot. No duplicate is
        created.
      </p>
      {(error || failed) && <Notice kind="error">{error ?? failed}</Notice>}
      <RecordSearch
        label="Find the person"
        placeholder="Name, company or email"
        kinds={['contact']}
        onPick={(item) => void choose(item)}
      />
      <p className="note" style={{ margin: 0 }}>
        Not found?{' '}
        <button
          type="button"
          className="linkbtn"
          onClick={() =>
            navigate(NAV.addContact.path, {
              replace: true,
              state: { background: { pathname: '/people/leads' }, kind: 'lead' },
            })
          }
        >
          New person
        </button>
        : type the person, then the project.
      </p>
    </Dialog>
  );
}

/**
 * + Lead, from the contact sheet: block 2 directly. `ask`: the seller back
 * from Hand the tablet, asked Is there a project? first.
 */
export function CreateLeadFromContact({ ask = false }: { ask?: boolean }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, error } = useContact(id);
  const back = () =>
    navigate({ pathname: `/people/contacts/${id}`, search: location.search }, { replace: true });
  if (!data) {
    return (
      <Dialog title="New lead" onClose={back}>
        {error ? (
          <Notice kind="error">{error}</Notice>
        ) : (
          <div className="spinner" aria-label="Loading" />
        )}
      </Dialog>
    );
  }
  return (
    <StartLead
      contact={data.contact}
      leads={data.leads}
      eventCode={data.eventCode}
      ask={ask}
      onCancel={back}
      onCreated={(lead) => navigate(leadSheetPath(lead), { replace: true })}
    />
  );
}

/** Link to an existing lead, from the contact sheet: the contact joins it, not primary. */
export function LinkToLead() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { save } = useOffline();
  const toast = useToast();
  const { data } = useContact(id);
  const back = () =>
    navigate({ pathname: `/people/contacts/${id}`, search: location.search }, { replace: true });

  async function pick(item: LinkItem) {
    if (!item.id) return;
    await save({
      opId: newId(),
      entity: 'lead_contact',
      action: 'link',
      leadId: item.id,
      contactId: id,
      changedAt: new Date().toISOString(),
    });
    toast(`Linked to ${item.label}.`);
    back();
  }

  return (
    <Dialog
      title="Link to an existing lead"
      onClose={back}
      footer={
        <button type="button" className="btn" onClick={back}>
          Cancel
        </button>
      }
    >
      <RecordSearch
        label="Find the lead"
        placeholder="Company or lead name"
        kinds={['lead']}
        exclude={data?.leads.map((l) => l.id) ?? []}
        onPick={(item) => void pick(item)}
      />
    </Dialog>
  );
}

/** + Add a contact, from the lead sheet: a portal contact joins the lead. */
export function AddContactToLead() {
  const { id = '' } = useParams();
  const { open } = usePeopleBack();
  const { save } = useOffline();
  const toast = useToast();
  const { data } = useLead(id);
  const back = () => open(`/people/leads/${id}`);

  const [failed, setFailed] = useState<string | null>(null);

  async function pick(item: LinkItem) {
    let contactId = item.id;
    try {
      if (!contactId && item.hubspotId) contactId = await importHubSpotContact(item.hubspotId);
    } catch (err) {
      setFailed(errorMessage(err));
      return;
    }
    if (!contactId) return;
    await save({
      opId: newId(),
      entity: 'lead_contact',
      action: 'link',
      leadId: id,
      contactId,
      changedAt: new Date().toISOString(),
    });
    toast(`Contact added: ${item.label}.`);
    back();
  }

  return (
    <Dialog
      title="Add a contact to this lead"
      onClose={back}
      footer={
        <button type="button" className="btn" onClick={back}>
          Cancel
        </button>
      }
    >
      {failed && <Notice kind="error">{failed}</Notice>}
      <RecordSearch
        label="Find the contact"
        placeholder="Name, company or email"
        kinds={['contact']}
        exclude={data?.contacts.map((c) => c.id) ?? []}
        onPick={(item) => void pick(item)}
      />
    </Dialog>
  );
}
